import type { ConsultationCreationMode } from '../calendar/types/Calendar';
import type {
  Consultation,
  ConsultationFieldDefinition,
  ConsultationFieldValue,
  ConsultationTemplate,
} from './types/Consultation';
import {
  createEmptyAttachmentsValue,
  hasConsultationAttachmentsValue,
  parseAttachmentsValue,
  serializeAttachmentsValue,
  validateConsultationAttachmentsValue,
} from './consultationAttachmentsSchema';
import {
  createEmptyVoiceValue,
  hasConsultationVoiceValue,
  parseVoiceValue,
  serializeVoiceValue,
} from './consultationVoiceSchema';
import {
  hasConsultationRepeaterValue,
  parseConsultationRepeaterSchema,
  serializeRepeaterValue,
  validateConsultationRepeaterValue,
  createEmptyRepeaterValue,
} from './consultationRepeaterSchema';

export type StructuredConsultationField = ConsultationFieldDefinition & {
  sectionKey?: string;
  assignmentSortOrder: number;
  assignmentRequired: boolean;
  assignmentSource?: 'template' | 'consultation';
  consultationAssignmentId?: string;
};

export const DEFAULT_CONSULTATION_SECTION_KEY = '__general__';

export type ConsultationFieldSection = {
  sectionKey: string;
  label: string;
  fields: StructuredConsultationField[];
};

export const normalizeConsultationSectionKey = (sectionKey?: string | null): string => {
  const trimmed = sectionKey?.trim();
  return trimmed ? trimmed : DEFAULT_CONSULTATION_SECTION_KEY;
};

export const humanizeConsultationSectionKey = (sectionKey: string): string => {
  if (sectionKey === DEFAULT_CONSULTATION_SECTION_KEY) {
    return 'General';
  }

  return sectionKey
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
    .trim();
};

export const groupStructuredConsultationFields = (
  fields: StructuredConsultationField[],
): ConsultationFieldSection[] => {
  const sections: ConsultationFieldSection[] = [];
  const sectionIndex = new Map<string, number>();

  for (const field of fields) {
    const key = normalizeConsultationSectionKey(field.sectionKey);
    let index = sectionIndex.get(key);

    if (index === undefined) {
      index = sections.length;
      sectionIndex.set(key, index);
      sections.push({
        sectionKey: key,
        label: humanizeConsultationSectionKey(key),
        fields: [],
      });
    }

    sections[index].fields.push(field);
  }

  return sections;
};

export const getMissingRequiredConsultationFieldIds = <TField extends ConsultationFieldLike>(
  fields: TField[],
  fieldValues: Record<string, string>,
  pendingFieldIds: ReadonlySet<string> = new Set<string>(),
): string[] => {
  return fields
    .filter((field) => (field.assignmentRequired ?? field.isRequired ?? false))
    .filter((field) => {
      const fieldId = field.id ?? '';
      if (fieldId && pendingFieldIds.has(fieldId)) {
        return false;
      }

      return !hasConsultationFieldValue(field.fieldType, fieldValues[fieldId], field.schemaJson);
    })
    .map((field) => field.id ?? '')
    .filter(Boolean);
};

export const getSectionKeysWithMissingRequiredFields = (
  fields: StructuredConsultationField[],
  fieldValues: Record<string, string>,
  pendingFieldIds: ReadonlySet<string> = new Set<string>(),
): string[] => {
  const missingFieldIds = new Set(getMissingRequiredConsultationFieldIds(fields, fieldValues, pendingFieldIds));
  const sectionKeys = new Set<string>();

  for (const field of fields) {
    if (missingFieldIds.has(field.id)) {
      sectionKeys.add(normalizeConsultationSectionKey(field.sectionKey));
    }
  }

  return Array.from(sectionKeys);
};

export const countMissingRequiredFieldsInSection = (
  section: ConsultationFieldSection,
  fieldValues: Record<string, string>,
  pendingFieldIds: ReadonlySet<string> = new Set<string>(),
): number => {
  return getMissingRequiredConsultationFieldIds(section.fields, fieldValues, pendingFieldIds).length;
};

export type ConsultationFieldAssignmentLike = {
  fieldDefinitionId: string;
  sectionKey?: string;
  sortOrder?: number;
  isRequired?: boolean;
  id?: string;
};

type ConsultationFieldLike = {
  id?: string;
  name: string;
  fieldType: string;
  options?: Array<{ id: string; label: string }>;
  validationJson?: string;
  schemaJson?: string;
  assignmentRequired?: boolean;
  isRequired?: boolean;
};

type StructuredValidationRules = {
  min?: number;
  max?: number;
  step?: number;
  minDate?: string;
  maxDate?: string;
  minTime?: string;
  maxTime?: string;
  minDateTime?: string;
  maxDateTime?: string;
  minLength?: number;
  maxLength?: number;
  regex?: string;
  format?: string;
};

const parseValidationRules = (validationJson?: string): StructuredValidationRules | null => {
  if (!validationJson?.trim()) {
    return null;
  }

  try {
    return JSON.parse(validationJson) as StructuredValidationRules;
  } catch {
    return null;
  }
};

const validateNumericRules = (value: number, rules: StructuredValidationRules): string | null => {
  if (typeof rules.min === 'number' && value < rules.min) {
    return `value must be >= ${rules.min}`;
  }

  if (typeof rules.max === 'number' && value > rules.max) {
    return `value must be <= ${rules.max}`;
  }

  if (typeof rules.step === 'number' && rules.step > 0) {
    const base = typeof rules.min === 'number' ? rules.min : 0;
    const delta = (value - base) / rules.step;
    const epsilon = 1e-9;
    if (Math.abs(delta - Math.round(delta)) > epsilon) {
      return `value must follow step ${rules.step}`;
    }
  }

  return null;
};

const validateTextRules = (value: string, rules: StructuredValidationRules): string | null => {
  if (typeof rules.minLength === 'number' && value.length < rules.minLength) {
    return `value length must be >= ${rules.minLength}`;
  }

  if (typeof rules.maxLength === 'number' && value.length > rules.maxLength) {
    return `value length must be <= ${rules.maxLength}`;
  }

  if (rules.regex?.trim()) {
    try {
      const pattern = new RegExp(rules.regex);
      if (!pattern.test(value)) {
        return 'value does not match required pattern';
      }
    } catch {
      // Ignore malformed regex metadata to preserve backward compatibility.
    }
  }

  const normalizedFormat = rules.format?.trim().toLowerCase();
  if (normalizedFormat === 'email') {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(value)) {
      return 'value must be a valid email address';
    }
  }

  if (normalizedFormat === 'url') {
    try {
      // eslint-disable-next-line no-new
      new URL(value);
    } catch {
      return 'value must be a valid absolute url';
    }
  }

  return null;
};

const parseDateValue = (value: string): string | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  if (parsed.toISOString().slice(0, 10) !== value) {
    return null;
  }

  return value;
};

const parseTimeToSeconds = (value: string): number | null => {
  const match = value.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] ?? '0');

  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || !Number.isInteger(seconds)) {
    return null;
  }

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
    return null;
  }

  return (hours * 3600) + (minutes * 60) + seconds;
};

const parseDateTimeMillis = (value: string): number | null => {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const formatDateTimeLocalInput = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');
  const seconds = String(value.getSeconds()).padStart(2, '0');

  return seconds === '00'
    ? `${year}-${month}-${day}T${hours}:${minutes}`
    : `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const normalizeBooleanValue = (rawValue: string | undefined): string | undefined => {
  const normalized = rawValue?.trim().toLowerCase();
  if (normalized === 'true' || normalized === 'false') {
    return normalized;
  }

  return undefined;
};

const normalizeMultiSelectValue = (rawValue: string | undefined): string | undefined => {
  if (!rawValue) {
    return undefined;
  }

  const values = rawValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  if (values.length === 0) {
    return undefined;
  }

  return Array.from(new Set(values)).join(',');
};

export const normalizeStructuredConsultationFieldValueForInput = (
  fieldType: string,
  rawValue: string | undefined,
): string => {
  if (!rawValue) {
    return '';
  }

  if (fieldType === 'boolean') {
    return normalizeBooleanValue(rawValue) ?? '';
  }

  if (fieldType === 'multiSelect') {
    return normalizeMultiSelectValue(rawValue) ?? '';
  }

  if (fieldType === 'repeater') {
    return rawValue ?? '';
  }

  if (fieldType === 'attachments') {
    if (!rawValue?.trim()) {
      return serializeAttachmentsValue(createEmptyAttachmentsValue());
    }

    return serializeAttachmentsValue(parseAttachmentsValue(rawValue));
  }

  if (fieldType === 'voice') {
    if (!rawValue?.trim()) {
      return serializeVoiceValue(createEmptyVoiceValue());
    }

    return serializeVoiceValue(parseVoiceValue(rawValue));
  }

  if (fieldType === 'dateTime') {
    const trimmed = rawValue.trim();
    if (!trimmed) {
      return '';
    }

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
      return trimmed;
    }

    const timestamp = parseDateTimeMillis(trimmed);
    if (timestamp === null) {
      return trimmed;
    }

    return formatDateTimeLocalInput(new Date(timestamp));
  }

  return rawValue;
};

export const normalizeStructuredConsultationFieldValueForStorage = (
  fieldType: string,
  rawValue: string | undefined,
): string | undefined => {
  if (fieldType === 'boolean') {
    return normalizeBooleanValue(rawValue);
  }

  if (fieldType === 'multiSelect') {
    return normalizeMultiSelectValue(rawValue);
  }

  if (fieldType === 'repeater') {
    if (!rawValue?.trim()) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(rawValue) as Record<string, unknown>;
      return serializeRepeaterValue(parsed);
    } catch {
      return rawValue.trim();
    }
  }

  if (fieldType === 'attachments') {
    if (!rawValue?.trim()) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(rawValue) as { attachments?: unknown[] };
      return serializeAttachmentsValue({ attachments: Array.isArray(parsed.attachments) ? parsed.attachments as any : [] });
    } catch {
      return rawValue.trim();
    }
  }

  if (fieldType === 'voice') {
    if (!rawValue?.trim()) {
      return undefined;
    }

    try {
      return serializeVoiceValue(parseVoiceValue(rawValue));
    } catch {
      return rawValue.trim();
    }
  }

  const normalized = rawValue?.trim();
  if (!normalized) {
    return undefined;
  }

  if (fieldType === 'dateTime') {
    const timestamp = parseDateTimeMillis(normalized);
    return timestamp === null ? normalized : new Date(timestamp).toISOString();
  }

  return normalized;
};

const validateDateRules = (value: string, rules: StructuredValidationRules): string | null => {
  const dateValue = parseDateValue(value);
  if (!dateValue) {
    return null;
  }

  const minDate = rules.minDate?.trim();
  if (minDate && parseDateValue(minDate) && dateValue < minDate) {
    return `value must be on or after ${minDate}`;
  }

  const maxDate = rules.maxDate?.trim();
  if (maxDate && parseDateValue(maxDate) && dateValue > maxDate) {
    return `value must be on or before ${maxDate}`;
  }

  return null;
};

const validateTimeRules = (value: string, rules: StructuredValidationRules): string | null => {
  const timeValue = parseTimeToSeconds(value);
  if (timeValue === null) {
    return null;
  }

  const minTime = rules.minTime?.trim();
  if (minTime) {
    const minTimeSeconds = parseTimeToSeconds(minTime);
    if (minTimeSeconds !== null && timeValue < minTimeSeconds) {
      return `value must be on or after ${minTime}`;
    }
  }

  const maxTime = rules.maxTime?.trim();
  if (maxTime) {
    const maxTimeSeconds = parseTimeToSeconds(maxTime);
    if (maxTimeSeconds !== null && timeValue > maxTimeSeconds) {
      return `value must be on or before ${maxTime}`;
    }
  }

  return null;
};

const validateDateTimeRules = (value: string, rules: StructuredValidationRules): string | null => {
  const dateTimeValue = parseDateTimeMillis(value);
  if (dateTimeValue === null) {
    return null;
  }

  const minDateTime = rules.minDateTime?.trim();
  if (minDateTime) {
    const minDateTimeMillis = parseDateTimeMillis(minDateTime);
    if (minDateTimeMillis !== null && dateTimeValue < minDateTimeMillis) {
      return `value must be on or after ${minDateTime}`;
    }
  }

  const maxDateTime = rules.maxDateTime?.trim();
  if (maxDateTime) {
    const maxDateTimeMillis = parseDateTimeMillis(maxDateTime);
    if (maxDateTimeMillis !== null && dateTimeValue > maxDateTimeMillis) {
      return `value must be on or before ${maxDateTime}`;
    }
  }

  return null;
};

export const validateStructuredConsultationFieldValue = <TField extends ConsultationFieldLike>(
  field: TField,
  rawValue: string | undefined,
): string | null => {
  if (field.fieldType === 'boolean') {
    if (!rawValue || rawValue === 'true' || rawValue === 'false') {
      return null;
    }
    return 'value must be true or false';
  }

  const value = rawValue?.trim() ?? '';
  if (!value) {
    return null;
  }

  if (field.fieldType === 'repeater') {
    return validateConsultationRepeaterValue(field.schemaJson, value);
  }

  if (field.fieldType === 'attachments') {
    return validateConsultationAttachmentsValue(field.schemaJson, value);
  }

  if (field.fieldType === 'singleSelect' && field.options?.length) {
    const ids = new Set(field.options.map((option) => option.id));
    if (!ids.has(value)) {
      return 'value must match one of the configured option ids';
    }
  }

  if (field.fieldType === 'multiSelect' && field.options?.length) {
    const ids = new Set(field.options.map((option) => option.id));
    const values = value.split(',').map((item) => item.trim()).filter(Boolean);
    const unique = new Set(values);
    if (unique.size !== values.length) {
      return 'value contains duplicate option ids';
    }
    if (values.some((item) => !ids.has(item))) {
      return 'value must only contain configured option ids';
    }
  }

  if (field.fieldType === 'number' && !/^-?\d+$/.test(value)) {
    return 'value must be a valid integer';
  }

  if (field.fieldType === 'decimal') {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return 'value must be a valid decimal number';
    }
  }

  if (field.fieldType === 'date' && !parseDateValue(value)) {
    return 'value must be in yyyy-MM-dd format';
  }

  if (field.fieldType === 'time' && parseTimeToSeconds(value) === null) {
    return 'value must be in HH:mm or HH:mm:ss format';
  }

  if (field.fieldType === 'dateTime' && parseDateTimeMillis(value) === null) {
    return 'value must be a valid date-time';
  }

  const rules = parseValidationRules(field.validationJson);
  if (!rules) {
    return null;
  }

  if (field.fieldType === 'number') {
    const numericValue = Number(value);
    return validateNumericRules(numericValue, rules);
  }

  if (field.fieldType === 'decimal') {
    const numericValue = Number(value);
    return validateNumericRules(numericValue, rules);
  }

  if (field.fieldType === 'text' || field.fieldType === 'longText') {
    return validateTextRules(value, rules);
  }

  if (field.fieldType === 'date') {
    return validateDateRules(value, rules);
  }

  if (field.fieldType === 'time') {
    return validateTimeRules(value, rules);
  }

  if (field.fieldType === 'dateTime') {
    return validateDateTimeRules(value, rules);
  }

  return null;
};

export const hasConsultationFieldValue = (
  fieldType: string,
  value: string | undefined,
  schemaJson?: string,
): boolean => {
  if (fieldType === 'boolean') {
    return value === 'true' || value === 'false';
  }

  if (fieldType === 'repeater') {
    return hasConsultationRepeaterValue(schemaJson, value);
  }

  if (fieldType === 'attachments') {
    return hasConsultationAttachmentsValue(schemaJson, value);
  }

  if (fieldType === 'voice') {
    return hasConsultationVoiceValue(value);
  }

  return Boolean(value?.trim());
};

export const getMissingRequiredConsultationFields = <TField extends ConsultationFieldLike>(
  fields: TField[],
  fieldValues: Record<string, string>,
  pendingFieldIds: ReadonlySet<string> = new Set<string>(),
): string[] => {
  return fields
    .filter((field) => (field.assignmentRequired ?? field.isRequired ?? false))
    .filter((field) => {
      const fieldId = field.id ?? '';
      if (fieldId && pendingFieldIds.has(fieldId)) {
        return false;
      }

      return !hasConsultationFieldValue(field.fieldType, fieldValues[fieldId], field.schemaJson);
    })
    .map((field) => field.name);
};

export const mergeStructuredConsultationFields = (
  template: ConsultationTemplate | null | undefined,
  definitions: ConsultationFieldDefinition[],
  consultationAssignments: ConsultationFieldAssignmentLike[] = [],
): StructuredConsultationField[] => {
  const definitionsById = new Map(definitions.map((definition) => [definition.id, definition]));
  const merged = new Map<string, StructuredConsultationField>();

  for (const templateField of template?.fields ?? []) {
    const definition = definitionsById.get(templateField.fieldDefinitionId);
    if (!definition) {
      continue;
    }

    merged.set(definition.id, {
      ...definition,
      sectionKey: templateField.sectionKey,
      assignmentSortOrder: templateField.sortOrder,
      assignmentRequired: templateField.isRequired,
      assignmentSource: 'template',
    });
  }

  for (const assignment of consultationAssignments) {
    if (merged.has(assignment.fieldDefinitionId)) {
      continue;
    }

    const definition = definitionsById.get(assignment.fieldDefinitionId);
    if (!definition) {
      continue;
    }

    merged.set(definition.id, {
      ...definition,
      sectionKey: assignment.sectionKey,
      assignmentSortOrder: assignment.sortOrder ?? 0,
      assignmentRequired: assignment.isRequired ?? false,
      assignmentSource: 'consultation',
      consultationAssignmentId: assignment.id,
    });
  }

  return Array.from(merged.values()).sort((left, right) => left.assignmentSortOrder - right.assignmentSortOrder);
};

export const buildStructuredConsultationFieldValues = (
  fields: StructuredConsultationField[],
  savedValues: ConsultationFieldValue[] = [],
): Record<string, string> => {
  const initialValues: Record<string, string> = {};

  fields.forEach((field) => {
    const saved = savedValues.find((value) => value.fieldDefinitionId === field.id);
    const repeaterSchema = field.fieldType === 'repeater' ? parseConsultationRepeaterSchema(field.schemaJson) : null;
    const attachmentsSchema = field.fieldType === 'attachments';
    const voiceSchema = field.fieldType === 'voice';
    const fallbackValue = repeaterSchema
      ? serializeRepeaterValue(createEmptyRepeaterValue(repeaterSchema))
      : attachmentsSchema
        ? serializeAttachmentsValue(createEmptyAttachmentsValue())
        : voiceSchema
          ? serializeVoiceValue(createEmptyVoiceValue())
          : field.defaultValue ?? '';

    initialValues[field.id] = normalizeStructuredConsultationFieldValueForInput(
      field.fieldType,
      saved?.value ?? fallbackValue,
    );
  });

  return initialValues;
};

type ConsultationAuditSource = {
  createdByEmployeeName?: string;
  updatedByEmployeeName?: string;
  createdByAi?: boolean;
  createdAt: string;
  updatedAt: string;
};

type ConsultationAuditTranslator = (
  key: string,
  options?: { author?: string; dateTime?: string; defaultValue?: string; ns?: string },
) => string;

export const resolveConsultationAuditAuthor = (
  consultation: ConsultationAuditSource,
  kind: 'created' | 'updated',
  t: ConsultationAuditTranslator,
): string => {
  const name = kind === 'created' ? consultation.createdByEmployeeName : consultation.updatedByEmployeeName;
  if (name?.trim()) {
    return name.trim();
  }

  if (consultation.createdByAi && kind === 'created') {
    return t('createdByAiLabel', { defaultValue: 'AI assistant' });
  }

  return t('unknownAuthor', { ns: 'common', defaultValue: 'Unknown author' });
};

export const formatConsultationAuditEntry = (
  consultation: ConsultationAuditSource,
  kind: 'created' | 'updated',
  formatDateTime: (value: string) => string,
  t: ConsultationAuditTranslator,
): string => {
  const author = resolveConsultationAuditAuthor(consultation, kind, t);
  const dateTime = formatDateTime(kind === 'created' ? consultation.createdAt : consultation.updatedAt);

  if (kind === 'created') {
    return t('createdBy', { author, dateTime, defaultValue: 'Created by {{author}} · {{dateTime}}' });
  }

  return t('updatedBy', { author, dateTime, defaultValue: 'Updated by {{author}} · {{dateTime}}' });
};

export const formatConsultationAuditSummary = (
  consultation: ConsultationAuditSource,
  kind: 'created' | 'updated',
  formatDateTime: (value: string) => string,
  t: ConsultationAuditTranslator,
): string => {
  const author = resolveConsultationAuditAuthor(consultation, kind, t);
  const dateTime = formatDateTime(kind === 'created' ? consultation.createdAt : consultation.updatedAt);
  return `${author} · ${dateTime}`;
};

const resolveEmployeeNameFromLookup = (
  employeeId: string | undefined,
  employeeNameById?: Readonly<Record<string, string>>,
): string | undefined => {
  if (!employeeId || !employeeNameById) {
    return undefined;
  }

  return employeeNameById[employeeId]?.trim() || undefined;
};

export const getRelatedConsultationSwitcherLabel = (
  item: Consultation,
  creationMode: ConsultationCreationMode | undefined,
  options?: {
    employeeNameById?: Readonly<Record<string, string>>;
    fallback?: string;
  },
): string => {
  const fallback = options?.fallback?.trim()
    || item.appointmentTypeName?.trim()
    || item.templateName?.trim()
    || item.assetName?.trim()
    || item.customerContactName?.trim()
    || item.contactName?.trim()
    || 'Consultation';

  switch (creationMode) {
    case 'perEmployee':
      return resolveEmployeeNameFromLookup(item.assignedEmployeeId, options?.employeeNameById) || fallback;
    case 'perContact':
      return item.customerContactName?.trim() || item.contactName?.trim() || fallback;
    case 'perAsset':
      return item.assetName?.trim() || fallback;
    default:
      return fallback;
  }
};